// The completion gate a task's checklist imposes (the task-checklist design,
// decision 6): every `check` item must be ticked AND carry the evidence it
// requires before the task may be marked done. One pure function set, read by
// the backend's `setStatus('done')` / `setCheck` and by every web surface that
// shows or enforces the gate, so the server and the button cannot disagree.
//
// Structural types on purpose: the backend's Zod-inferred item and the
// client's `IChecklistItem` both satisfy them without either importing the
// other.

export type ChecklistEvidenceKind = 'photo' | 'comment' | 'blackboard';
export type ChecklistFieldModeLike = 'none' | 'optional' | 'required';

/** The evidence a tick carries (or is about to carry). */
export interface IChecklistEvidenceLike {
  attachedPhoto?: unknown;
  comment?: string;
  blackboard?: { workType?: string; location?: string; date?: string };
}

export interface IChecklistCompletionLike extends IChecklistEvidenceLike {
  checked: boolean;
}

export interface IChecklistItemLike {
  id: string;
  kind: 'description' | 'check';
  photo: ChecklistFieldModeLike;
  comment: ChecklistFieldModeLike;
  blackboard: ChecklistFieldModeLike;
  completion?: IChecklistCompletionLike;
}

/** The evidence a check can ask for, in the order every surface shows it. */
export const EVIDENCE_KINDS: readonly ChecklistEvidenceKind[] = ['photo', 'comment', 'blackboard'];

type EvidenceModes = Pick<IChecklistItemLike, 'kind' | ChecklistEvidenceKind>;

/** Whether ticking this item asks for any evidence at all (a capture step). */
export const asksForEvidence = (item: EvidenceModes): boolean =>
  item.kind === 'check' && EVIDENCE_KINDS.some((kind) => item[kind] !== 'none');

/** Whether the tick is refused without some evidence. */
export const requiresEvidence = (item: EvidenceModes): boolean =>
  item.kind === 'check' && EVIDENCE_KINDS.some((kind) => item[kind] === 'required');

function hasEvidence(kind: ChecklistEvidenceKind, evidence: IChecklistEvidenceLike | undefined): boolean {
  if (kind === 'photo') return !!evidence?.attachedPhoto;
  if (kind === 'comment') return !!evidence?.comment?.trim();
  const board = evidence?.blackboard;
  return !!(board?.workType?.trim() || board?.location?.trim() || board?.date?.trim());
}

/** The evidence kinds `item` requires that `evidence` does not carry. */
export function missingEvidence(
  item: Pick<IChecklistItemLike, ChecklistEvidenceKind>,
  evidence: IChecklistEvidenceLike | undefined,
): ChecklistEvidenceKind[] {
  return EVIDENCE_KINDS.filter((kind) => item[kind] === 'required' && !hasEvidence(kind, evidence));
}

/** Whether a `check` item still stands between its task and done: unticked,
 *  or ticked without its required evidence. Description items never do. */
export function isCheckOpen(item: IChecklistItemLike): boolean {
  if (item.kind !== 'check') return false;
  if (!item.completion?.checked) return true;
  return missingEvidence(item, item.completion).length > 0;
}

/** Ids of the checks that block completion, in the order given. */
export function openCheckIds(items: readonly IChecklistItemLike[] | undefined): string[] {
  return (items ?? []).filter(isCheckOpen).map((i) => i.id);
}

/** Whether the checklist blocks the task from being marked done. */
export function checklistBlocksCompletion(items: readonly IChecklistItemLike[] | undefined): boolean {
  return openCheckIds(items).length > 0;
}

/** How many checks still block completion — the count the surfaces display
 *  next to a disabled Mark done. `remainingRequiredChecks(x) === 0 ⟺
 *  !checklistBlocksCompletion(x)` by construction. */
export function remainingRequiredChecks(items: readonly IChecklistItemLike[] | undefined): number {
  return openCheckIds(items).length;
}

export interface IChecklistProgress {
  /** Checks that no longer block (ticked with their evidence). */
  done: number;
  /** Every `check` item; description items are guidance, not progress. */
  total: number;
}

/** `done/total` over the checks — the chip and the Done-when meter. */
export function checklistProgress(items: readonly IChecklistItemLike[] | undefined): IChecklistProgress {
  const checks = (items ?? []).filter((i) => i.kind === 'check');
  return { done: checks.filter((i) => !isCheckOpen(i)).length, total: checks.length };
}
